import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { Truck, Shield, AlertCircle } from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';
// import { useAddress } from '../../contexts/AddressContext';
import type { DeliveryOption } from '../../types';

interface CartSummaryProps {
  showCheckoutButton?: boolean;
  isProcessing?: boolean;
  selectedDeliveryOption?: DeliveryOption | null;
  validationErrors?: string[];
  // onClose?: () => void;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  showCheckoutButton = true,
  isProcessing = false,
  selectedDeliveryOption = null,
  validationErrors = [],
  // onClose
}) => {
  const { cart } = useCart();
  const [deliveryFee, setDeliveryFee] = useState(0);
  // const { addresses, fetchAddresses, isLoading } = useAddress();

  const total = cart.subtotal + deliveryFee;
  // const isEligibleForFreeDelivery = false; //cart.subtotal >= 100;

  useEffect(() => {
    setDeliveryFee(selectedDeliveryOption?.fee || 0);
  }, [selectedDeliveryOption]);

  return (
    <div className="bg-white rounded-lg border p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">
        Order Summary
      </h2>

      <dl className="space-y-3">
        <div className="flex justify-between">
          <dt className="text-sm text-gray-600">Subtotal</dt>
          <dd className="text-sm font-medium text-gray-900">
            {formatPrice(cart.subtotal)}
          </dd>
        </div>

        <div className="flex justify-between">
          <dt className="text-sm text-gray-600">Delivery</dt>
          <dd className="text-sm font-medium text-gray-900">
            {!selectedDeliveryOption ? (
              <span className="text-gray-400">--.--</span>
            ) : deliveryFee === 0 ? (
              <span className="text-success-600">Free</span>
            ) : (
              `${formatPrice(deliveryFee)}`
            )}
          </dd>
        </div>

        {/* Show selected delivery type */}
        {selectedDeliveryOption && (
          <div className="text-xs text-gray-500 mt-1 text-right">
            {selectedDeliveryOption.type === 'fastest' ? 'Fastest delivery' : 'Economy delivery'}
          </div>
        )}

        {/* {!isEligibleForFreeDelivery && cart.subtotal > 0 && (
          <div className="text-sm text-success-600 bg-success-50 p-2 rounded">
            Add {formatPrice((100 - cart.subtotal))} more for free delivery!
          </div>
        )} */}

        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between">
            <dt className="text-base font-medium text-gray-900">Total</dt>
            <dd className="text-base font-bold text-gray-900">
              {formatPrice(total)}
            </dd>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Cash on delivery
          </p>
        </div>
      </dl>
      {showCheckoutButton && (
        validationErrors.length === 0 ? (
          <Link
            to="/checkout"
            className={`mt-6 w-full py-3 px-4 rounded-lg text-lg font-medium flex items-center justify-center transition-colors ${cart.items.length === 0 || isProcessing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
          >
            {isProcessing ? 'Processing...' : 'Proceed to Checkout'}
          </Link>
        ) : (
          <div className="mt-6 w-full">
            <button
              onClick={() => {
                // Scroll to first error or expand error list
                const errorElement = document.querySelector('.error-list');
                if (errorElement) {
                  errorElement.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="w-full py-3 px-4 rounded-lg text-lg font-medium flex items-center justify-center bg-red-50 text-red-600 border-2 border-red-200 hover:bg-red-100 transition-colors"
            >
              <AlertCircle className="w-5 h-5 mr-2" />
              {validationErrors.length} {validationErrors.length === 1 ? 'issue' : 'issues'} - Fix to Checkout
            </button>
            <p className="text-xs text-red-500 mt-1 text-center">Please fix the errors in red</p>
          </div>
        )
      )}

      {/* Features */}
      <div className="mt-6 space-y-3">
        <div className="flex items-center text-sm text-gray-600">
          <Truck className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
          <span>
            Enjoy fast and reliable delivery with package tracking.
            {/* {isEligibleForFreeDelivery
              ? 'Free delivery eligible'
              : 'Free delivery on orders over $100'
            } */}
          </span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Shield className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
          <span>Cash on delivery available</span>
        </div>
      </div>

      {/* Continue Shopping Link */}
      {cart.items.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <Link
            to="/products"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            ← Continue Shopping
          </Link>
        </div>
      )}
    </div>
  );
};

export default CartSummary;